import axios, { AxiosError } from 'axios'
import type {
  User,
  Video,
  TrackingLink,
  ViewEvent,
  DashboardData,
  CreateLinkFormData,
  UpdateLinkFormData,
  CreateVideoFormData,
} from '../types'

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Don't redirect on 401 - let React Router handle auth redirects
    // The authStore.checkAuth() handles setting user to null on 401
    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post<{ user: User }>('/auth/login', { email, password })
    return response.data.user
  },

  logout: async () => {
    await api.post('/auth/logout')
  },

  getMe: async () => {
    const response = await api.get<{ user: User }>('/auth/me')
    return response.data.user
  },

  register: async (email: string, password: string, name: string, company?: string) => {
    const response = await api.post<{ user: User }>('/auth/register', {
      email,
      password,
      name,
      company,
    })
    return response.data.user
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    await api.put('/auth/password', {
      current_password: currentPassword,
      new_password: newPassword,
    })
  },
}

// Videos API
export const videosApi = {
  list: async (params?: { page?: number; per_page?: number; search?: string; status?: string }) => {
    const response = await api.get<{
      videos: Video[]
      total: number
      pages: number
      page: number
    }>('/videos', { params })
    return response.data
  },

  get: async (id: string, includeLinks = false) => {
    const response = await api.get<{ video: Video }>(`/videos/${id}`, {
      params: { include_links: includeLinks },
    })
    return response.data.video
  },

  create: async (data: CreateVideoFormData) => {
    const response = await api.post<{ video: Video }>('/videos', data)
    return response.data.video
  },

  update: async (id: string, data: Partial<CreateVideoFormData>) => {
    const response = await api.put<{ video: Video }>(`/videos/${id}`, data)
    return response.data.video
  },

  delete: async (id: string) => {
    await api.delete(`/videos/${id}`)
  },

  getUploadUrl: async (id: string, fileSize: number, fileName?: string) => {
    try {
      const response = await api.post<{ upload_url: string }>(`/videos/${id}/upload-url`, {
        file_size: fileSize,
        file_name: fileName,
      })
      return response.data.upload_url
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error)
      }
      throw error
    }
  },

  markUploadComplete: async (id: string) => {
    const response = await api.post<{ video: Video }>(`/videos/${id}/upload-complete`)
    return response.data.video
  },

  getDownloadUrl: async (id: string) => {
    const response = await api.post<{ download_url: string; status: string }>(`/videos/${id}/download`)
    return response.data
  },

  getLinks: async (id: string) => {
    const response = await api.get<{ links: TrackingLink[] }>(`/videos/${id}/links`)
    return response.data.links
  },

  createLink: async (videoId: string, data: CreateLinkFormData) => {
    const response = await api.post<{ link: TrackingLink }>(`/videos/${videoId}/links`, data)
    return response.data.link
  },
}

// Links API
export const linksApi = {
  list: async (params?: { page?: number; per_page?: number; video_id?: string }) => {
    const response = await api.get<{
      links: TrackingLink[]
      total: number
      pages: number
      page: number
    }>('/links', { params })
    return response.data
  },

  get: async (id: string, includeEvents = false) => {
    const response = await api.get<{ link: TrackingLink }>(`/links/${id}`, {
      params: { include_events: includeEvents },
    })
    return response.data.link
  },

  update: async (id: string, data: UpdateLinkFormData) => {
    const response = await api.put<{ link: TrackingLink }>(`/links/${id}`, data)
    return response.data.link
  },

  delete: async (id: string) => {
    await api.delete(`/links/${id}`)
  },

  getEvents: async (id: string, params?: { page?: number; per_page?: number }) => {
    const response = await api.get<{
      events: ViewEvent[]
      total: number
      pages: number
      page: number
    }>(`/links/${id}/events`, { params })
    return response.data
  },

  regenerateCode: async (id: string) => {
    const response = await api.post<{ link: TrackingLink }>(`/links/${id}/regenerate`)
    return response.data.link
  },
}

// Analytics API
export const analyticsApi = {
  getDashboard: async (days = 30) => {
    const response = await api.get<DashboardData>('/analytics/dashboard', { params: { days } })
    return response.data
  },

  getVideoAnalytics: async (videoId: string, days = 30) => {
    const response = await api.get(`/analytics/video/${videoId}`, { params: { days } })
    return response.data
  },

  getLinkAnalytics: async (linkId: string, days = 30) => {
    const response = await api.get(`/analytics/link/${linkId}`, { params: { days } })
    return response.data
  },
}

// Settings API
export const settingsApi = {
  getProfile: async () => {
    const response = await api.get<{ profile: User }>('/settings/profile')
    return response.data.profile
  },

  updateProfile: async (data: { name?: string; company?: string; avatar_url?: string }) => {
    const response = await api.put<{ profile: User }>('/settings/profile', data)
    return response.data.profile
  },

  getNotifications: async () => {
    const response = await api.get<{
      notifications: {
        email: boolean
        sms: boolean
        slack: boolean
        phone_number?: string
        slack_user_id?: string
      }
    }>('/settings/notifications')
    return response.data.notifications
  },

  updateNotifications: async (data: {
    email?: boolean
    sms?: boolean
    slack?: boolean
    phone_number?: string
  }) => {
    const response = await api.put('/settings/notifications', data)
    return response.data.notifications
  },
}

// AI API
export const aiApi = {
  summarize: async (videoId: string) => {
    const response = await api.post(`/ai/summarize/${videoId}`)
    return response.data
  },

  getMessageSuggestions: async (linkId: string) => {
    const response = await api.get<{ suggestions: string[] }>(`/ai/message-suggestions/${linkId}`)
    return response.data.suggestions
  },

  getEngagementInsights: async (videoId: string) => {
    const response = await api.get(`/ai/engagement-insights/${videoId}`)
    return response.data
  },
}

export default api
